import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import { TIPO_CAMPANHA_LABEL, TipoCampanhaBack } from '../enum/tipo-campanha-back.enum';
import { CampanhaStatus } from '../enum/campanha-status.enum';
import { RelatorioFormato } from '../enum/relatorio-formato.enum';
import { Adesao, AdesaoFiltros } from '../models/adesao.model';
import { CampanhaFormValue } from '../models/campanha-form.model';
import { Campanha, CampanhaFiltros } from '../models/campanha.model';
import { DestinoCampanha } from '../models/destino-campanha.model';
import { ImportarParticipantesResponse } from '../models/participante-importacao.model';
import { Plano } from '../models/plano.model';
import { TipoCampanha } from '../models/tipo-campanha.model';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import {
  CampanhaPlanoElegivelResponseDto,
  CampanhaResponseDto,
  PerfilParticipanteQueryParams,
  PlanoResponseDto,
  TipoCampanhaResponseDto,
} from '../api/models';
import {
  CampanhaCommandMapperOptions,
  adesaoStatusParaCodigo,
  mapCampanhaFiltrosToQueryParams,
  mapCampanhaFormValueToCreateCommand,
  mapCampanhaFormValueToUpdateCommand,
  mapCampanhaResponseToCampanha,
  mapCampanhaResponseToFormValue,
  mapImportarParticipantesResponseToModel,
  mapPerfilParticipanteToAdesao,
  mapQuestionarioResponseToDestinoCampanha,
} from '../api/mappers/campanha-api.mapper';
import { EnumStatusResponse } from '../enum/enumHttpResponse';
import { CampanhaApiService } from './campanha-api.service';

const STATUS_PADRAO_LISTAGEM = CampanhaStatus.AtivoPortal;

interface ApiCampanhaEditContext {
  codCampanha?: number;
  seq?: number;
  codEmprs?: number | null;
  dtaEfetivacao?: string | null;
  dtaAlteracao?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CampanhaService {
  constructor(private readonly campanhaApiService: CampanhaApiService) {}
  private readonly apiEditContext = new Map<string, ApiCampanhaEditContext>();

  listarCampanhas(filtros?: CampanhaFiltros): Observable<Campanha[]> {
    const filtrosEfetivos: CampanhaFiltros = {
      status: STATUS_PADRAO_LISTAGEM,
      ...filtros,
    };

    const queryParams = mapCampanhaFiltrosToQueryParams(filtrosEfetivos);

    // O catálogo (TiposCampanhas) continua sendo buscado, mas o rótulo exibido não depende
    // mais dele — vem de TIPO_CAMPANHA_LABEL (decisão do PO, 2026-08-10). O forkJoin fica por
    // ora só para manter a chamada acontecendo; o resultado de listarTiposCampanha() não é
    // usado no map abaixo.
    return forkJoin([
      this.campanhaApiService.listCampanhas(queryParams),
      this.listarTiposCampanha(),
    ]).pipe(
      map(([dtos]) =>
        // A listagem so mostra Suitability. O corte usa codCampanha (codigo estavel do back), nao
        // o texto do catalogo, que vem formatado como "5 -  Suitability" e mudaria com a redacao.
        dtos
          .filter((dto) => dto.codCampanha === TipoCampanhaBack.Suitability)
          .map((dto) => mapCampanhaResponseToCampanha(dto)),
      ),
      map((campanhas) => this.filtrarCampanhas(filtrosEfetivos, campanhas)),
    );
  }

  /** Catálogo real de tipos de campanha (GET /v1/Campanha/TiposCampanhas, PRE_TBL_TIP_CAMPANHA). */
  listarTiposCampanha(): Observable<TipoCampanha[]> {
    return this.campanhaApiService
      .listTiposCampanha()
      .pipe(map((tipos) => tipos.map((dto) => this.mapTipoCampanhaResponseToTipoCampanha(dto))));
  }

  obterCampanha(id: string): Observable<Campanha | undefined> {
    const parsedId = Number.parseInt(id, 10);
    if (Number.isNaN(parsedId)) {
      return of(undefined);
    }

    return this.campanhaApiService.getCampanha(parsedId).pipe(
      map((dto) => mapCampanhaResponseToCampanha(dto)),
      catchError((error: unknown) => {
        if (
          error instanceof ApiHttpError &&
          error.status === EnumStatusResponse.NotFound
        ) {
          return of(undefined);
        }

        return throwError(() => error);
      }),
    );
  }

  obterCampanhaParaEdicao(id: string): Observable<CampanhaFormValue | undefined> {
    const parsedId = Number.parseInt(id, 10);
    if (Number.isNaN(parsedId)) {
      return of(undefined);
    }

    return this.campanhaApiService.getCampanha(parsedId).pipe(
      switchMap((dto) => {
        this.cacheApiEditContext(id, dto);
        const formValue = mapCampanhaResponseToFormValue(dto);

        if (!formValue.linkCampanha.trim()) {
          formValue.linkCampanha = this.gerarLinkCampanha(mapCampanhaResponseToCampanha(dto));
        }

        return this.loadPlanosElegiveisForEdit(parsedId).pipe(
          map(({ planos, selecionarTodos }) => {
            // A lista é preservada mesmo com "todos os planos" marcado: são os vínculos que a
            // campanha tem de fato, e a tela de visualização os exibe nos dois casos.
            if (planos.length > 0) {
              formValue.planosAdicionados = planos;
            }

            formValue.selecionarTodosPlanos = selecionarTodos;

            return formValue;
          }),
        );
      }),
      catchError((error: unknown) => {
        if (
          error instanceof ApiHttpError &&
          error.status === EnumStatusResponse.NotFound
        ) {
          return of(undefined);
        }

        return throwError(() => error);
      }),
    );
  }

  prepararCampanhaClonada(id: string): Observable<CampanhaFormValue | undefined> {
    return this.obterCampanhaParaEdicao(id).pipe(
      map((formValue) =>
        formValue
          ? { ...formValue, id: undefined, nome: `Cópia de ${formValue.nome}`, linkCampanha: '' }
          : undefined,
      ),
    );
  }

  excluirCampanha(id: string): Observable<boolean> {
    const parsedId = Number.parseInt(id, 10);
    if (Number.isNaN(parsedId)) {
      return of(false);
    }

    return this.campanhaApiService.deleteCampanha(parsedId).pipe(
      map(() => {
        this.apiEditContext.delete(id);
        return true;
      }),
      catchError((error: unknown) => {
        if (
          error instanceof ApiHttpError &&
          error.status === EnumStatusResponse.NotFound
        ) {
          return of(false);
        }

        if (
          error instanceof ApiHttpError &&
          error.status === EnumStatusResponse.NotAcceptable
        ) {
          const detail = error.problem?.detail?.trim();
          const message =
            detail ||
            'Esta campanha possui participantes ou importações vinculados e não pode ser excluída.';
          return throwError(() => new ApiHttpError(error.status, message, error.problem));
        }

        return throwError(() => error);
      }),
    );
  }

  listarPlanos(): Observable<Plano[]> {
    return this.campanhaApiService
      .listPlanos()
      .pipe(map((planos) => planos.map((dto) => this.mapPlanoResponseToPlano(dto))));
  }

  listarPlanosDisponiveis(): Observable<Plano[]> {
    return this.listarPlanos();
  }

  salvarRascunho(formValue: CampanhaFormValue): Observable<Campanha> {
    if (formValue.id) {
      return this.atualizarCampanhaViaApi(formValue, CampanhaStatus.Rascunho);
    }

    return this.criarCampanhaViaApi(formValue, CampanhaStatus.Rascunho);
  }

  /** Agenda a campanha (R→AG) — não a ativa imediatamente no portal. */
  agendarCampanha(formValue: CampanhaFormValue): Observable<Campanha> {
    if (formValue.id) {
      return this.atualizarCampanhaViaApi(formValue, CampanhaStatus.Agendada);
    }

    return this.criarCampanhaViaApi(formValue, CampanhaStatus.Agendada);
  }

  importarParticipantes(
    campanhaId: number,
    arquivo: File,
  ): Observable<ImportarParticipantesResponse> {
    return this.campanhaApiService
      .importarParticipantesElegiveis(campanhaId, arquivo)
      .pipe(map((dto) => mapImportarParticipantesResponseToModel(dto)));
  }

  gerarLinkCampanha(campanha: Campanha): string {
    const slug = campanha.nome
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const path = slug || `campanha-${campanha.id}`;
    return `vivest.com.br/${path}`;
  }

  listarDestinos(): Observable<DestinoCampanha[]> {
    return this.campanhaApiService.listQuestionarios({ status: 'P' }).pipe(
      map((dtos) => dtos.map((dto) => mapQuestionarioResponseToDestinoCampanha(dto))),
    );
  }

  /** Adesões da campanha no recorte da tela — o filtro vai na query, o back devolve a lista pronta. */
  listarAdesoes(campanhaId: string, filtros?: AdesaoFiltros): Observable<Adesao[]> {
    const parsedId = Number.parseInt(campanhaId, 10);
    if (Number.isNaN(parsedId)) {
      return of([]);
    }

    return this.campanhaApiService
      .listParticipantes(this.montarFiltroParticipantes(parsedId, filtros))
      .pipe(map((dtos) => dtos.map((dto) => mapPerfilParticipanteToAdesao(dto))));
  }

  /**
   * Planilha das adesões, no mesmo recorte da tela: nome, status e data da confirmação do termo
   * vão como filtro quando estão preenchidos.
   */
  exportarAdesoes(campanhaId: string, filtros?: AdesaoFiltros): Observable<Blob> {
    const parsedId = Number.parseInt(campanhaId, 10);
    if (Number.isNaN(parsedId)) {
      return throwError(
        () =>
          new ApiHttpError(
            EnumStatusResponse.UnknownError,
            'Campanha inválida para exportação.',
          ),
      );
    }

    return this.campanhaApiService.exportarParticipantes(
      this.montarFiltroParticipantes(parsedId, filtros),
    );
  }

  /** Filtros da tela no formato que os endpoints de participante esperam. */
  private montarFiltroParticipantes(
    idCampanha: number,
    filtros?: AdesaoFiltros,
  ): PerfilParticipanteQueryParams {
    return {
      idCampanha,
      nomeParticipante: filtros?.nome?.trim() || undefined,
      // O endpoint recebe o código de uma letra, não o texto que a tela exibe.
      status: adesaoStatusParaCodigo(filtros?.status),
      dataConfirmacaoTermo: filtros?.data || undefined,
    };
  }

  /** Relatório da campanha em PDF ou Excel. */
  exportarRelatorio(campanhaId: string, formato: RelatorioFormato): Observable<Blob> {
    const parsedId = Number.parseInt(campanhaId, 10);
    if (Number.isNaN(parsedId)) {
      return throwError(
        () =>
          new ApiHttpError(
            EnumStatusResponse.UnknownError,
            'Campanha inválida para o relatório.',
          ),
      );
    }

    return this.campanhaApiService.exportarRelatorio(parsedId, formato);
  }

  private cacheApiEditContext(id: string, dto: CampanhaResponseDto): void {
    this.apiEditContext.set(id, {
      codCampanha: dto.codCampanha,
      seq: dto.seq,
      codEmprs: dto.codEmprs ?? null,
      dtaEfetivacao: dto.dtaEfetivacao ?? null,
      dtaAlteracao: dto.dtaAlteracao ?? null,
    });
  }

  private atualizarCampanhaViaApi(
    formValue: CampanhaFormValue,
    cdStatus: CampanhaStatus,
  ): Observable<Campanha> {
    const parsedId = Number.parseInt(formValue.id ?? '', 10);
    if (Number.isNaN(parsedId)) {
      return throwError(
        () =>
          new ApiHttpError(
            EnumStatusResponse.UnknownError,
            'Invalid campaign id for API update.',
          ),
      );
    }

    // Contexto cacheado pelo GET de edição (`obterCampanhaParaEdicao` →
    // `cacheApiEditContext`). Quando o PUT é disparado sem GET prévio (ex.: publicar
    // direto sem reabrir a edição) não há contexto: usa-se um fallback explícito —
    // `codCampanha = TipoCampanhaBack.Suitability`, `seq = 1`, `codEmprs = null` e sem
    // datas de efetivação/alteração (campos só enviados quando vieram do GET). Coberto por spec.
    const context = this.apiEditContext.get(formValue.id!) ?? {};
    const codCampanha = context.codCampanha ?? TipoCampanhaBack.Suitability;
    if (codCampanha <= 0) {
      return throwError(
        () =>
          new ApiHttpError(
            EnumStatusResponse.UnknownError,
            'Campaign parent code (codCampanha) is invalid for API update.',
          ),
      );
    }

    const commandOptions: CampanhaCommandMapperOptions = {
      cdStatus,
      codCampanha,
      seq: context.seq ?? 1,
      codEmprs: context.codEmprs ?? null,
    };

    if (formValue.id && this.apiEditContext.has(formValue.id)) {
      commandOptions.dtaEfetivacao = context.dtaEfetivacao ?? null;
      commandOptions.dtaAlteracao = context.dtaAlteracao ?? null;
    }

    const command = mapCampanhaFormValueToUpdateCommand(formValue, commandOptions);

    return this.campanhaApiService.updateCampanha(parsedId, command).pipe(
      switchMap((dto) => {
        if (formValue.id) {
          this.cacheApiEditContext(formValue.id, dto);
        }

        const campanha = mapCampanhaResponseToCampanha(dto);
        return this.syncPlanosAfterCampanhaMutation(parsedId, formValue, campanha);
      }),
    );
  }

  private criarCampanhaViaApi(
    formValue: CampanhaFormValue,
    cdStatus: CampanhaStatus,
  ): Observable<Campanha> {
    const command = mapCampanhaFormValueToCreateCommand(formValue, {
      cdStatus,
      codCampanha: TipoCampanhaBack.Suitability,
      seq: 1,
    });

    return this.campanhaApiService.createCampanha(command).pipe(
      switchMap((dto) => {
        const campanha = mapCampanhaResponseToCampanha(dto);
        const campanhaId = dto.idCampanha;

        if (campanhaId == null) {
          return of(campanha);
        }

        return this.syncPlanosAfterCampanhaMutation(campanhaId, formValue, campanha);
      }),
    );
  }

  private syncPlanosAfterCampanhaMutation(
    campanhaId: number,
    formValue: CampanhaFormValue,
    campanha: Campanha,
  ): Observable<Campanha> {
    if (!formValue.selecionarTodosPlanos && formValue.planosAdicionados.length === 0) {
      return of(campanha);
    }

    return this.syncPlanosViaApi(
      campanhaId,
      formValue.planosAdicionados,
      formValue.selecionarTodosPlanos,
    ).pipe(map(() => campanha));
  }

  private resolveIdRegra(campanhaId: number): Observable<number> {
    return this.campanhaApiService.listRegrasElegibilidade(campanhaId).pipe(
      switchMap((regras) => {
        const existingRegraId = regras.find((regra) => regra.idCampanhaRegra != null)
          ?.idCampanhaRegra;

        if (existingRegraId != null) {
          return of(existingRegraId);
        }

        return this.campanhaApiService.createRegraElegibilidade(campanhaId).pipe(
          switchMap((created) => {
            if (created.idCampanhaRegra == null) {
              return throwError(
                () =>
                  new ApiHttpError(
                    EnumStatusResponse.UnknownError,
                    'API did not return idCampanhaRegra after creating eligibility rule.',
                  ),
              );
            }

            return of(created.idCampanhaRegra);
          }),
        );
      }),
    );
  }

  private syncPlanosViaApi(
    campanhaId: number,
    planos: Plano[],
    selecionarTodosPlanos: boolean,
  ): Observable<void> {
    // `selecionarTodosPlanos` manda `planos: []`: o backend sincroniza com todos os planos
    // vigentes (mesma definição usada no catálogo que preenche a tela). Fora disso, manda a
    // lista exata da tela — o backend faz o diff (adiciona/remove) numa única transação.
    const desiredNums = selecionarTodosPlanos
      ? []
      : planos.map((plano) => this.planoToNumPlbnf(plano)).filter((num): num is number => num != null);

    return this.resolveIdRegra(campanhaId).pipe(
      switchMap((idRegra) => this.campanhaApiService.syncPlanosElegiveis(campanhaId, idRegra, desiredNums)),
      map(() => undefined),
    );
  }

  private loadPlanosElegiveisForEdit(
    campanhaId: number,
  ): Observable<{ planos: Plano[]; selecionarTodos: boolean }> {
    return this.campanhaApiService.listRegrasElegibilidade(campanhaId).pipe(
      switchMap((regras) => {
        const idRegra = regras.find((regra) => regra.idCampanhaRegra != null)?.idCampanhaRegra;
        if (idRegra == null) {
          return of({ planos: [], selecionarTodos: false });
        }

        return this.campanhaApiService.listPlanosElegiveis(campanhaId, idRegra).pipe(
          switchMap((apiPlanos) => {
            const planos = apiPlanos.map((plano) => this.mapPlanoElegivelToPlano(plano));

            if (planos.length === 0) {
              return of({ planos, selecionarTodos: false });
            }

            // Sem flag dedicada no backend, "todos os planos" é inferido comparando
            // a contagem de planos vinculados com o catálogo total (`GET /v1/Planos`).
            return this.campanhaApiService.listPlanos().pipe(
              map((catalogo) => ({
                planos,
                selecionarTodos: catalogo.length > 0 && planos.length >= catalogo.length,
              })),
            );
          }),
        );
      }),
    );
  }

  private mapPlanoElegivelToPlano(dto: CampanhaPlanoElegivelResponseDto): Plano {
    const numPlbnf = dto.numPlbnf ?? 0;

    return {
      id: String(numPlbnf),
      nome: dto.dcrPlbnf?.trim() || `Plano ${numPlbnf}`,
      codigo: String(numPlbnf),
    };
  }

  private mapPlanoResponseToPlano(dto: PlanoResponseDto): Plano {
    return {
      id: String(dto.numPlbnf),
      nome: dto.dcrPlbnf?.trim() || `Plano ${dto.numPlbnf}`,
      codigo: String(dto.numPlbnf),
    };
  }

  /** Rótulo sempre curto (decisão do PO, 2026-08-10): TIPO_CAMPANHA_LABEL ganha do texto do
   * catálogo; a API só decide para códigos que a UI ainda não conhece. */
  private mapTipoCampanhaResponseToTipoCampanha(dto: TipoCampanhaResponseDto): TipoCampanha {
    const codigo = dto.codCampanha ?? 0;
    return {
      codigo,
      descricao:
        TIPO_CAMPANHA_LABEL[codigo as TipoCampanhaBack] ??
        (dto.dcrCampanha?.trim() || dto.descCampanha?.trim() || String(codigo)),
    };
  }

  private planoToNumPlbnf(plano: Plano): number | null {
    const fromCodigo = Number.parseInt(plano.codigo, 10);
    if (Number.isFinite(fromCodigo) && fromCodigo > 0) {
      return fromCodigo;
    }

    const fromId = Number.parseInt(plano.id, 10);
    if (Number.isFinite(fromId) && fromId > 0) {
      return fromId;
    }

    return null;
  }

  private filtrarCampanhas(filtros: CampanhaFiltros, campanhas: Campanha[]): Campanha[] {
    return campanhas.filter((campanha) => {
      if (filtros.nome?.trim()) {
        const termo = filtros.nome.trim().toLowerCase();
        if (!campanha.nome.toLowerCase().includes(termo)) {
          return false;
        }
      }

      if (filtros.idCampanha != null && campanha.id !== String(filtros.idCampanha)) {
        return false;
      }

      if (filtros.status && filtros.status !== 'todos') {
        if (campanha.status !== filtros.status) {
          return false;
        }
      }

      if (!this.campanhaDentroDoPeriodo(campanha, filtros.periodoDe, filtros.periodoAte)) {
        return false;
      }

      if (filtros.tipo && filtros.tipo !== 'todos') {
        if (campanha.tipo !== filtros.tipo) {
          return false;
        }
      }

      return true;
    });
  }

  private campanhaDentroDoPeriodo(
    campanha: Campanha,
    periodoDe?: string,
    periodoAte?: string,
  ): boolean {
    if (periodoDe && campanha.dataFim < periodoDe) {
      return false;
    }

    if (periodoAte && campanha.dataInicio > periodoAte) {
      return false;
    }

    return true;
  }
}
